import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../grid/gridView';
import {
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_TITLE,
} from '../../../webcomponents.constants';
import { GridEntry } from '../types/phasr-recommendations-grid-entry';
import { GridViewMetadata, SelectionStoreMode, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { GridConfig } from './GridConfig';
import { DefaultViewProps, ErrorCodes } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { Column, ColumnKeys } from '../types/grid';
import { GzBackendError } from '../../../accounts/common/types/GzBackendError';
import { htmlEncode } from '../../../utils/escape.utils';
import { Filter } from '../../../grid/gridFilters/types';

export class PhasrRecommendationsGridView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $grid: GridViewWebComponent<Column, ColumnKeys, GridEntry>;
    $title: HTMLElement;
    $hostElement: HTMLElement;
    $detailsPanel: GzSideMenuWebComponent;
    $behavioralProfilesPanel: GzSideMenuWebComponent;
  };

  public getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $grid: this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<Column, ColumnKeys>,
      $title: this.shadow.querySelector(WEB_COMPONENT_GZ_TITLE),
      $detailsPanel: this.shadow.querySelector('#recommendationDetailsPanel') as GzSideMenuWebComponent,
      $behavioralProfilesPanel: this.shadow.querySelector('#behavioralProfilesPanel') as GzSideMenuWebComponent,
      $hostElement: this.shadow,
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public setTranslatedStrings(): void {
    this.dom.$title.textContent = translateFn('PHASR recommendations');
  }

  public async buildSuccessView(): Promise<void> {
    const gridConfig = new GridConfig();
    const {
      viewMetaData,
      defaultViewProps
    } = await gridConfig.getDataForView(
      this.dependencies,
      this.dom
    );
    const defaultFilters = gridConfig.getDefaultFilters();

    this.buildGrid(viewMetaData, defaultViewProps, defaultFilters);
  }

  private buildGrid(viewMetaData: GridViewMetadata<Column, ColumnKeys>, defaultViewData: DefaultViewProps, defaultFilters: Filter<ColumnKeys>[]): void {
    const defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
      compactView: defaultViewData.compactView,
      displayedColumns: defaultViewData.displayedColumns,
      filters: defaultViewData.filters,
      name: '',
      pageSize: defaultViewData.pageSize,
      type: ViewType.DEFAULT,
      id: viewMetaData.viewId,
    };

    this.dom.$grid.propertiesSet = false;
    this.dom.$grid.sideMenuParent = this.dom.$hostElement;
    this.dom.$grid.selectionStoreConfig = {
      functionalMode: SelectionStoreMode.RECORDS,
    };

    this.dom.$grid.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('No recommendations'),
    };

    for (const [key, value] of Object.entries(viewMetaData)) {
      this.dom.$grid[key] = value;
    }
    this.dom.$grid.defaultViewProps = defaultViewProps;
    this.dom.$grid.onDataLoadingFinishedWithError = error => this.setGridErrorMessage(error);
    this.addPivotingFilterValues(defaultFilters);
    this.dom.$grid.propertiesSet = true;
    this.dom.$grid.injectDependencies(this.dependencies);
    this.dom.$grid.start().catch();
  }

  public async setGridErrorMessage(error: GzBackendError | undefined) {
    let dataLoadingFailedMessage = htmlEncode(
      translateFn(
        'Data loading has failed. Please try to refresh the page and if the issue persists contact Bitdefender Enterprise Support for more information.'
      )
    );

    if (error?.err?.code === ErrorCodes.COMPANY_DOES_NOT_EXIST_OR_IS_NOT_ACCESSIBLE) {
      dataLoadingFailedMessage = translateFn(
        'You have companies in your saved filters that do not exist or are not accessible anymore. Change your company filter and try again.'
      );
    }

    this.dom.$grid.customStrings.dataLoadingFailed = dataLoadingFailedMessage;
    this.dom.$grid.controller?.updateCustomStrings();
  }

  private addPivotingFilterValues(defaultFilters: Filter<ColumnKeys>[]): void {
    if (!this.dependencies.navigationParams) {
      return;
    }

    const existingFilters = defaultFilters;

    for (const filter of this.dependencies.navigationParams.filters) {
      let keyExists = false;
      const filterKey = Object.keys(filter)[0];
      const filterValue = filter[filterKey];

      if (filterKey === ColumnKeys.COMPANY && !this.dependencies.permissions.canHaveChildCompanies) {
        continue;
      }
      if (filterKey === ColumnKeys.PHASR_CATEGORIES) {
        filterValue.in = filterValue.in.map((category: string) => parseInt(category, 10));
      }

      for (const [index, existingFilter] of existingFilters.entries()) {
        if (Object.keys(existingFilter)[0] === filterKey) {
          existingFilters[index][filterKey] = filterValue;
          keyExists = true;
        }
      }

      if (!keyExists) {
        existingFilters.push(filter);
      }
    }

    this.dom.$grid.filters = existingFilters;

    this.dependencies.navigationParams = null;
  }

  public getCompanyIdFilterValue(): string {
    if (this.dependencies.permissions.canHaveChildCompanies) {
      //In View, the first filter is set to be the company filter
      return this.dom.$grid.filters[0].company?.exactMatch ?? '';
    } else {
      return '';
    }
  }
}
